/**
 * Core vocabulary shared by every layer. Nothing here knows about HTTP,
 * Postgres, React, or a realtime provider.
 */

export type Id = string;

// ─── §17.1 Session states ────────────────────────────────────────────────────
export const SESSION_STATES = [
  "Draft",
  "Lobby",
  "Active",
  "Paused",
  "Completed",
  "Cancelled",
] as const;
export type SessionState = (typeof SESSION_STATES)[number];

// ─── §17.2 Question-round states ─────────────────────────────────────────────
export const ROUND_STATES = [
  "Available",
  "Reading",
  "Open",
  "Closed",
  "Grading",
  "Results",
  "Completed",
  "Cancelled",
  "Restored",
] as const;
export type RoundState = (typeof ROUND_STATES)[number];

// ─── §17.3 Player states ─────────────────────────────────────────────────────
export const PLAYER_STATES = [
  "Joining",
  "Lobby",
  "Ready",
  "Answering",
  "Submitted",
  "Results",
  "Waiting",
  "Disconnected",
  "Reconnecting",
] as const;
export type PlayerState = (typeof PLAYER_STATES)[number];

// ─── §6.5 Board cell states ──────────────────────────────────────────────────
export const CELL_STATES = [
  "Available",
  "Selected",
  "Answering",
  "Completed",
  "Cancelled",
  "Restored",
] as const;
export type CellState = (typeof CELL_STATES)[number];

/** §6.5 — round state to the cell state the board renders for it. */
export function cellStateForRound(round: RoundState): CellState {
  switch (round) {
    case "Reading":
      return "Selected";
    case "Open":
      return "Answering";
    case "Closed":
    case "Grading":
    case "Results":
    case "Completed":
      return "Completed";
    case "Cancelled":
      return "Cancelled";
    case "Restored":
      return "Restored";
    default:
      return "Available";
  }
}

// ─── Answering ───────────────────────────────────────────────────────────────
export const ANSWER_MODES = ["Both", "TextOnly", "MultipleChoiceOnly"] as const;
export type AnswerMode = (typeof ANSWER_MODES)[number];

export const ANSWER_METHODS = ["typed", "multiple_choice", "none"] as const;
export type AnswerMethod = (typeof ANSWER_METHODS)[number];

/** §10.4 */
export const GRADING_OUTCOMES = ["correct", "partial", "incorrect", "no_answer"] as const;
export type GradingOutcome = (typeof GRADING_OUTCOMES)[number];

/** §10.1 — which layer produced a verdict. */
export type GradingLayer = 1 | 2 | 3 | 4;

export type GradingSource =
  | "normalize"
  | "deterministic"
  | "similarity"
  | "choice"
  | "no_answer"
  | "cache"
  | `provider:${string}`
  | "manual";

// ─── Identity and authorization vocabulary (§27.3) ───────────────────────────
export const ROLES = ["platform_admin", "tenant_admin", "facilitator", "player"] as const;
export type Role = (typeof ROLES)[number];

export const ACTOR_TYPES = [
  "platform_admin",
  "tenant_admin",
  "facilitator",
  "player",
  "system",
  "api_client",
] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

export interface Actor {
  /** Never null — §27.3 actor attribution on writes. */
  actorType: ActorType;
  actorId: string;
  tenantId: Id;
  roles: Role[];
}

// ─── Tenancy (§35) ───────────────────────────────────────────────────────────
export const TENANT_STATUSES = ["active", "suspended", "archived"] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

// ─── Avatars (§36) ───────────────────────────────────────────────────────────
export const AVATAR_STATUSES = ["pending", "generating", "ready", "failed", "declined"] as const;
export type AvatarStatus = (typeof AVATAR_STATUSES)[number];

export const AVATAR_KINDS = ["stylized_photo", "animal", "source_photo", "local_fallback"] as const;
export type AvatarKind = (typeof AVATAR_KINDS)[number];

// ─── Deployment (§19) ────────────────────────────────────────────────────────
export const DEPLOYMENT_PROFILES = ["cloud", "local_event"] as const;
export type DeploymentProfile = (typeof DEPLOYMENT_PROFILES)[number];

// ─── Board shapes ────────────────────────────────────────────────────────────
export interface BoardCell {
  questionId: Id;
  cellOrder: number;
  cellValue: number;
  state: CellState;
}

export interface BoardCategory {
  categoryId: Id;
  name: string;
  displayOrder: number;
  cells: BoardCell[];
}

export interface BoardState {
  gameVersionId: Id;
  valuePrefix: string;
  categories: BoardCategory[];
  completedCount: number;
  totalCount: number;
}
